import { MedicalResponse } from '@/modules/surgicalEvent/baseMedical.service';
import {
  DASHBOARD_PAGE_SIZE,
  EarlyPostopDto,
  GenericDashboardDto,
  PeropStatus,
  PeropAllDto,
  PostopAllDto,
  PostopChronicPainDto,
  PostopFilters,
  PreopAllDto,
  PreopAnemicDto,
  PreopCardiacStressDto,
  PreopChronicPainDto,
  PreopDayViewFilters,
  PreopLabTestingDto,
  PreopSleepApneaDto,
  PreopSmokerDto,
  PreopStatus,
  PreopSurgeryPlanningDto,
  PreopSurgeryPlanningFilters,
  PreopTelehealthDto,
  PreopView
} from '@/modules/surgicalEvent/dashboard/dashboard.model';
import axios, { AxiosResponse } from 'axios';
import {
  ListApiResponse,
  SingleApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import {
  AnemiaProtocolStatus,
  CardiacStressTestProtocolStatus,
  EarlyPostopProtocolStatus,
  NicotineProtocolStatus,
  NutritionSupportProtocolStatus,
  PostopChronicPainProtocolStatus,
  PreopChronicPainProtocolStatus,
  PreopLabTestProtocolStatus,
  SleepApneaProtocolStatus,
  SurgeryPlanningProtocolStatus,
  TelehealthProtocolStatus
} from '@/modules/protocol';
import omit from 'lodash/fp/omit';
import { captureThrowOnDev, toKebabQuery } from '@/util';
import { removeEmptyValues, XOR } from '@ianesthesie-kit/core';
import { SentryService } from '@ianesthesie-kit/sentry';

export type Pagination = {
  page?: number;
  pageSize?: number;
};

export enum PreopSortBy {
  PROCEDURE = 'procedure',
  CPA = 'cpa',
  ASA = 'asa',
  GLOBAL_RISK = 'global-risk',
  PRIORITY = 'priority'
}

export type Sorting = {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

export type StatusWithPagination<
  T extends string,
  K extends string = 'status'
> = {
  [k in K]: T | T[];
} & Pagination;

export type StatusWithPaginationSorted<
  T extends string,
  K extends string = 'status'
> = StatusWithPagination<T, K> & Sorting;

export type SurgeryPlanningParams =
  StatusWithPaginationSorted<SurgeryPlanningProtocolStatus> &
    PreopSurgeryPlanningFilters;

export type PostopParams = Pagination & Sorting & PostopFilters;

export type Grouped<T, K extends string = string> = Record<K, T[]>;
type AxiosGrouped<T, K extends string = string> = AxiosResponse<
  SingleApiResponse<Grouped<T, K>>
>;
export type MedicalGrouped<T, K extends string = string> = MedicalResponse<
  Grouped<T, K>,
  false
>;

export type Listed<T> = Omit<ListApiResponse<T>, 'ok'> & Pagination;
type AxiosListed<T> = AxiosResponse<ListApiResponse<T>>;
export type MedicalListed<T> = MedicalResponse<Listed<T>, false>;

type FetchPreopConsultationParam = Omit<
  StatusWithPaginationSorted<PreopStatus>,
  'status'
> &
  XOR<
    { status: PreopStatus },
    { specificDate: string } & PreopDayViewFilters
  > & {
    preopView: PreopView;
  };

export class DashboardService {
  private static readonly INSTANCE: DashboardService = new DashboardService();

  private readonly url: string;

  private constructor() {
    this.url = '/bff/doctor/surgical-event';
  }

  public static getInstance(): DashboardService {
    return this.INSTANCE;
  }

  public fetchPreopConsultations(
    params: FetchPreopConsultationParam
  ): Promise<MedicalListed<PreopAllDto>> {
    // TODO this check is for debugging on sentry
    // remove it after finding the actual cause
    let err: Error | undefined = undefined;
    if (!params.preopView) {
      err = new Error('Preop view param not present');
    } else if (!params.specificDate && !params.status) {
      err = new Error(
        `Either specific date or status must be provided (preopView: ${params.preopView})`
      );
    } else if (params.preopView === PreopView.UPCOMING && !params.status) {
      err = new Error(`PreopView is UPCOMING but status is not present`);
    } else if (params.preopView === PreopView.DAY && !params.specificDate) {
      err = new Error(`PreopView is DAY but specificDate is not present`);
    }
    if (err) {
      SentryService.getInstance()
        .addBreadcrumb({
          message: `Location: ${location.href}`,
          level: 'info'
        })
        .addBreadcrumb({
          message: 'Params used',
          data: params,
          level: 'info'
        });
      captureThrowOnDev(err);
    }

    const reqParams = {
      ...omit(
        ['status', 'specificDate', 'preopView', 'validated'],
        removeEmptyValues(params)
      ),
      dateGroup:
        params.preopView === PreopView.UPCOMING ? params.status : undefined,
      date:
        params.preopView === PreopView.DAY ? params.specificDate : undefined,
      dateType: 'CONSULTATION',
      validated: params.validated && params.validated === 'VALIDATED'
    };

    return axios
      .get(this.endpoint('/preop', reqParams))
      .then((response: AxiosListed<PreopAllDto>) =>
        this.transformPatients(response)
      )
      .catch((e) => {
        throw unwrapApiErrorDetails(e) || e;
      });
  }

  public fetchLabTestingPreops(
    params: StatusWithPaginationSorted<PreopLabTestProtocolStatus>
  ): Promise<MedicalListed<PreopLabTestingDto>> {
    return axios
      .get(this.endpoint('/preop/lab-testing', params))
      .then((response: AxiosListed<PreopLabTestingDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchSurgeryPlanningPreops(
    params: SurgeryPlanningParams
  ): Promise<MedicalListed<PreopSurgeryPlanningDto>> {
    return axios
      .get(
        this.endpoint('/preop/surgery-planning', this.transformFilters(params))
      )
      .then((response: AxiosListed<PreopSurgeryPlanningDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchCardiacStressTestPreops(
    params: StatusWithPaginationSorted<CardiacStressTestProtocolStatus>
  ): Promise<MedicalListed<PreopCardiacStressDto>> {
    return axios
      .get(this.endpoint('/preop/cardiac-stress-test', params))
      .then((response: AxiosListed<PreopCardiacStressDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchTelehealthPreops(
    params: StatusWithPaginationSorted<TelehealthProtocolStatus>
  ): Promise<MedicalListed<PreopTelehealthDto>> {
    return axios
      .get(this.endpoint('/preop/telehealth', params))
      .then((response: AxiosListed<PreopTelehealthDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchSmokerPreops(
    params: StatusWithPaginationSorted<NicotineProtocolStatus>
  ): Promise<MedicalListed<PreopSmokerDto>> {
    return axios
      .get(this.endpoint('/preop/smoker', params))
      .then((response: AxiosListed<PreopSmokerDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchOsaRiskPreops(
    params: StatusWithPaginationSorted<SleepApneaProtocolStatus>
  ): Promise<MedicalListed<PreopSleepApneaDto>> {
    return axios
      .get(this.endpoint('/preop/osa-risk', params))
      .then((response: AxiosListed<PreopSleepApneaDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchAnemicPreops(
    params: StatusWithPaginationSorted<AnemiaProtocolStatus>
  ): Promise<MedicalListed<PreopAnemicDto>> {
    return axios
      .get(this.endpoint('/preop/anemic', params))
      .then((response: AxiosListed<PreopAnemicDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchMalnutritionPreops(
    params: StatusWithPaginationSorted<NutritionSupportProtocolStatus>
  ): Promise<MedicalListed<GenericDashboardDto>> {
    return axios
      .get(this.endpoint('/preop/malnutrition', params))
      .then((response: AxiosListed<GenericDashboardDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchChronicPainPreops(
    params: StatusWithPaginationSorted<PreopChronicPainProtocolStatus>
  ): Promise<MedicalListed<PreopChronicPainDto>> {
    return axios
      .get(this.endpoint('/preop/chronic-pain', params))
      .then((response: AxiosListed<PreopChronicPainDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchPostops(
    params: PostopParams
  ): Promise<MedicalListed<PostopAllDto>> {
    const transformed = this.transformFilters(params);
    return axios
      .get(this.endpoint('/postop', transformed))
      .then((response: AxiosListed<PostopAllDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchEarlyPostops(
    params: StatusWithPagination<EarlyPostopProtocolStatus>
  ): Promise<MedicalListed<EarlyPostopDto>> {
    return axios
      .get(this.endpoint('/postop/early', params))
      .then((response: AxiosListed<EarlyPostopDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchChronicPainPostops(
    params: StatusWithPaginationSorted<PostopChronicPainProtocolStatus>
  ): Promise<MedicalListed<PostopChronicPainDto>> {
    return axios
      .get(this.endpoint('/postop/chronic-pain', params))
      .then((response: AxiosListed<PostopChronicPainDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public fetchPerops(params?: {
    search: string | undefined;
  }): Promise<MedicalGrouped<PeropAllDto, PeropStatus>> {
    return axios
      .get(this.endpoint('/perop', params))
      .then((response: AxiosGrouped<PeropAllDto>) =>
        this.transformPatients(response)
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  private endpoint(segm = '', p?: Pagination & Partial<Sorting> & any): string {
    if (!p) {
      return `${this.url}${segm}`;
    }
    const { sortBy, sortDirection, ...restParams } = p;
    const sort = sortBy ? `${sortBy}:${sortDirection || 'asc'}` : undefined;
    let page = p.page ?? 0;
    let pageSize = p.pageSize ?? DASHBOARD_PAGE_SIZE;
    if (pageSize < 0 || page < 0) {
      captureThrowOnDev(
        new Error(
          `Invalid pagination params: page=${page}, pageSize=${pageSize}`
        )
      );
    }
    page = Math.max(page, 0);
    pageSize = Math.max(pageSize, 0);
    const params = toKebabQuery({
      ...restParams,
      page,
      pageSize,
      sort
    });
    return `${this.url}${segm}${params}`;
  }

  private transformFilters(filters: SurgeryPlanningParams): any;
  private transformFilters(filters: PostopParams): any;
  private transformFilters(filters: SurgeryPlanningParams | PostopParams) {
    if (!filters) {
      return filters;
    }

    const dateFilter = (filters as any).date?.dateFrom && (filters as any).date;
    const severityFilter = (filters as any).severity;
    const commentsFilter =
      (filters as any).comments === 'NOT_BLANK' ? true : undefined;

    return {
      ...omit(['date', 'severity', 'comments'], filters),
      ...dateFilter,
      severity: severityFilter,
      comments: commentsFilter
    };
  }

  private transformPatients<
    T extends GenericDashboardDto,
    K extends string = string
  >(response: AxiosGrouped<T>): MedicalGrouped<T, K>;

  private transformPatients<T extends GenericDashboardDto>(
    response: AxiosListed<T>
  ): MedicalListed<T>;

  private transformPatients<
    T extends GenericDashboardDto,
    K extends string = string
  >(
    response: AxiosGrouped<T> | AxiosListed<T>
  ): MedicalGrouped<T, K> | MedicalListed<T> {
    if (Array.isArray(response.data.data)) {
      return {
        data: {
          count: response.data.count,
          data: response.data.data.map((dto) => this.transformPatient(dto))
        }
      } as MedicalListed<T>;
    }

    const data = response.data.data;
    Object.values(data)
      .flat()
      .forEach((dto) => this.transformPatient(dto));
    return { data };
  }

  private transformPatient<T extends GenericDashboardDto>(dto: T): T {
    const facePicture = (dto.patient as any)?.facePictureMedium;
    delete (dto.patient as any)?.facePictureMedium;
    return {
      ...dto,
      patient: {
        ...dto.patient,
        facePicture
      }
    };
  }
}
